import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  UtensilsCrossed,
  Plus,
  Calendar,
  Users,
  Clock,
  ChefHat,
  Coffee,
  Sun,
  Moon,
  Cookie,
  CheckCircle,
  AlertCircle,
  BarChart3,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MealManagement = () => {
  const [meals, setMeals] = useState([]);
  const [staff, setStaff] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportPeriod, setReportPeriod] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showServeDialog, setShowServeDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [mealToDelete, setMealToDelete] = useState(null);
  const [serveData, setServeData] = useState({
    residents_adults: 0,
    residents_children: 0,
    community_adults: 0,
    community_children: 0
  });
  
  const [formData, setFormData] = useState({
    meal_type: 'breakfast',
    date: new Date().toISOString().split('T')[0], // Initial date
    menu_items: [''],
    assigned_staff: [],
    assigned_residents: [],
    notes: '',
    expected_attendance: 0,
    ingredients: []
  });
  const [inventory, setInventory] = useState([]);
  const [foodCost, setFoodCost] = useState(null);

  useEffect(() => {
    fetchMeals();
    fetchStaff();
    fetchResidents();
    // Update form date when selected date changes
    setFormData(prev => ({
      ...prev,
      date: selectedDate
    }));
  }, [selectedDate]);

  const fetchMeals = async () => {
    try {
      const response = await axios.get(`${API}/meals?date=${selectedDate}`);
      setMeals(response.data);
    } catch (error) {
      console.error('Error fetching meals:', error);
      toast.error('Failed to load meals');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      // Assuming we have a staff endpoint, for now we'll use users
      const response = await axios.get(`${API}/residents`); // Replace with staff endpoint when available
      setStaff([]);
      setResidents(response.data.filter(r => r.status === 'current'));
    } catch (error) {
      console.error('Error fetching staff/residents:', error);
    }
  };

  const fetchResidents = async () => {
    try {
      const response = await axios.get(`${API}/residents?status=current`);
      setResidents(response.data);
    } catch (error) {
      console.error('Error fetching residents:', error);
    }
  };

  useEffect(() => {
    axios.get(`${API}/inventory-items`).then((r) => setInventory(r.data)).catch(() => {});
    const start = `${selectedDate.slice(0, 7)}-01`;
    const end = `${selectedDate.slice(0, 7)}-31`;
    axios.get(`${API}/reports/food-cost?start_date=${start}&end_date=${end}`).then((r) => setFoodCost(r.data)).catch(() => {});
  }, [selectedDate, meals]);

  // Ingredient row helpers
  const addIngredient = () => setFormData((p) => ({ ...p, ingredients: [...p.ingredients, { inventory_item_id: '', name: '', quantity: 0, unit: 'each', unit_cost: 0, portion_per_person: '' }] }));
  const removeIngredient = (i) => setFormData((p) => ({ ...p, ingredients: p.ingredients.filter((_, x) => x !== i) }));
  const updateIngredient = (i, field, value) => setFormData((p) => {
    const ings = [...p.ingredients];
    ings[i] = { ...ings[i], [field]: value };
    if (field === 'inventory_item_id' && value) {
      const item = inventory.find((it) => it.id === value);
      if (item) { ings[i].name = item.name; ings[i].unit = item.unit; ings[i].unit_cost = item.unit_cost; }
    }
    return { ...p, ingredients: ings };
  });
  const liveMealCost = formData.ingredients.reduce((s, ing) => s + (parseFloat(ing.quantity) || 0) * (parseFloat(ing.unit_cost) || 0), 0);
  const liveCostPerServing = formData.expected_attendance > 0 ? liveMealCost / formData.expected_attendance : 0;

  // Current residents with dietary restrictions / food allergies — surfaced on the
  // meal-planning screen so kitchen staff see them while building the menu.
  const dietaryAlerts = residents.filter(
    (r) => (r.dietary_restrictions && r.dietary_restrictions.trim()) || (r.other_allergies && r.other_allergies.trim())
  );

  const deductInventory = async (meal) => {
    try {
      await axios.post(`${API}/meals/${meal.id}/deduct-inventory`);
      toast.success('Ingredients deducted from inventory');
      fetchMeals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not deduct'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const mealData = {
        ...formData,
        menu_items: formData.menu_items.filter(item => item.trim() !== ''),
        expected_attendance: parseInt(formData.expected_attendance) || 0,
        ingredients: formData.ingredients
          .filter((ing) => (ing.name || '').trim() !== '')
          .map((ing) => ({
            inventory_item_id: ing.inventory_item_id || null,
            name: ing.name,
            quantity: parseFloat(ing.quantity) || 0,
            unit: ing.unit || 'each',
            unit_cost: parseFloat(ing.unit_cost) || 0,
            portion_per_person: ing.portion_per_person ? parseFloat(ing.portion_per_person) : null,
          }))
      };
      
      await axios.post(`${API}/meals`, mealData);
      toast.success('Meal scheduled successfully');
      resetForm();
      fetchMeals();
    } catch (error) {
      console.error('Error creating meal:', error);
      toast.error('Failed to schedule meal');
    }
  };

  const handleServeMeal = async () => {
    if (!selectedMeal) return;
    
    try {
      await axios.put(`${API}/meals/${selectedMeal.id}/serve`, serveData);
      const total = serveData.residents_adults + serveData.residents_children + 
                   serveData.community_adults + serveData.community_children;
      toast.success(`Recorded ${total} meals served`);
      
      setShowServeDialog(false);
      setSelectedMeal(null);
      setServeData({
        residents_adults: 0,
        residents_children: 0,
        community_adults: 0,
        community_children: 0
      });
      fetchMeals();
    } catch (error) {
      console.error('Error serving meal:', error);
      toast.error('Failed to record meal service');
    }
  };

  const resetForm = () => {
    setFormData({
      meal_type: 'breakfast',
      date: selectedDate, // Use the selected date instead of hardcoded today
      menu_items: [''],
      assigned_staff: [],
      assigned_residents: [],
      notes: '',
      expected_attendance: 0,
      ingredients: []
    });
    setShowAddDialog(false);
  };

  const handleDeleteMeal = async () => {
    if (!mealToDelete) return;
    
    try {
      await axios.delete(`${API}/meals/${mealToDelete.id}`);
      toast.success('Meal deleted successfully');
      
      setShowDeleteDialog(false);
      setMealToDelete(null);
      fetchMeals();
    } catch (error) {
      console.error('Error deleting meal:', error);
      toast.error('Failed to delete meal');
    }
  };

  const generateMonthlyReport = async () => {
    try {
      setLoading(true);
      const startDate = `${reportPeriod.year}-${String(reportPeriod.month).padStart(2, '0')}-01`;
      const endDate = new Date(reportPeriod.year, reportPeriod.month, 0).toISOString().split('T')[0];
      
      // Fetch all meals for the selected month
      const response = await axios.get(`${API}/meals?start_date=${startDate}&end_date=${endDate}`);
      const monthlyMeals = response.data;
      
      // Calculate statistics
      const stats = {
        totalMeals: monthlyMeals.length,
        totalResidentsAdults: 0,
        totalResidentsChildren: 0,
        totalCommunityAdults: 0,
        totalCommunityChildren: 0,
        totalServed: 0,
        breakfastCount: 0,
        lunchCount: 0,
        dinnerCount: 0,
        snackCount: 0,
        dailyBreakdown: {},
        mealTypeBreakdown: {},
        menuItems: []
      };

      monthlyMeals.forEach(meal => {
        // Count meal types
        stats[`${meal.meal_type}Count`]++;
        
        // Count people served
        if (meal.residents_adults) stats.totalResidentsAdults += meal.residents_adults;
        if (meal.residents_children) stats.totalResidentsChildren += meal.residents_children;
        if (meal.community_adults) stats.totalCommunityAdults += meal.community_adults;
        if (meal.community_children) stats.totalCommunityChildren += meal.community_children;
        
        // Daily breakdown
        const date = meal.date;
        if (!stats.dailyBreakdown[date]) {
          stats.dailyBreakdown[date] = {
            breakfast: 0, lunch: 0, dinner: 0, snack: 0,
            totalServed: 0, residents: 0, community: 0
          };
        }
        
        stats.dailyBreakdown[date][meal.meal_type]++;
        const mealTotal = (meal.residents_adults || 0) + (meal.residents_children || 0) + 
                         (meal.community_adults || 0) + (meal.community_children || 0);
        stats.dailyBreakdown[date].totalServed += mealTotal;
        stats.dailyBreakdown[date].residents += (meal.residents_adults || 0) + (meal.residents_children || 0);
        stats.dailyBreakdown[date].community += (meal.community_adults || 0) + (meal.community_children || 0);
        
        // Menu items
        if (meal.menu_items) {
          stats.menuItems = [...stats.menuItems, ...meal.menu_items];
        }
      });

      stats.totalServed = stats.totalResidentsAdults + stats.totalResidentsChildren + 
                         stats.totalCommunityAdults + stats.totalCommunityChildren;
      stats.totalResidents = stats.totalResidentsAdults + stats.totalResidentsChildren;
      stats.totalCommunity = stats.totalCommunityAdults + stats.totalCommunityChildren;

      setReportData(stats);
      setShowReportDialog(true);
      
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate monthly report');
    } finally {
      setLoading(false);
    }
  };

  const exportReportToCSV = () => {
    if (!reportData) return;

    const monthName = new Date(reportPeriod.year, reportPeriod.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    const csvData = [
      ['Monthly Meal Service Report', monthName],
      [''],
      ['SUMMARY STATISTICS'],
      ['Total Meals Served', reportData.totalMeals],
      ['Total People Served', reportData.totalServed],
      [''],
      ['BREAKDOWN BY POPULATION'],
      ['Shelter Residents (Adults)', reportData.totalResidentsAdults],
      ['Shelter Residents (Children)', reportData.totalResidentsChildren],
      ['Community Members (Adults)', reportData.totalCommunityAdults],
      ['Community Members (Children)', reportData.totalCommunityChildren],
      ['Total Residents', reportData.totalResidents],
      ['Total Community', reportData.totalCommunity],
      [''],
      ['BREAKDOWN BY MEAL TYPE'],
      ['Breakfast Meals', reportData.breakfastCount],
      ['Lunch Meals', reportData.lunchCount],
      ['Dinner Meals', reportData.dinnerCount],
      ['Snack Services', reportData.snackCount],
      [''],
      ['DAILY BREAKDOWN'],
      ['Date', 'Breakfast', 'Lunch', 'Dinner', 'Snacks', 'Total Served', 'Residents', 'Community']
    ];

    Object.entries(reportData.dailyBreakdown).sort().forEach(([date, data]) => {
      csvData.push([
        new Date(date + 'T00:00:00').toLocaleDateString(),
        data.breakfast,
        data.lunch, 
        data.dinner,
        data.snack,
        data.totalServed,
        data.residents,
        data.community
      ]);
    });

    const csvContent = csvData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Monthly_Meal_Report_${monthName.replace(' ', '_')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Monthly report exported successfully');
  };

  const printReport = () => {
    window.print();
  };

  const addMenuItem = () => {
    setFormData({
      ...formData,
      menu_items: [...formData.menu_items, '']
    });
  };

  const updateMenuItem = (index, value) => {
    const newItems = [...formData.menu_items];
    newItems[index] = value;
    setFormData({
      ...formData,
      menu_items: newItems
    });
  };

  const removeMenuItem = (index) => {
    const newItems = formData.menu_items.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      menu_items: newItems.length > 0 ? newItems : ['']
    });
  };

  const getMealIcon = (mealType) => {
    switch (mealType) {
      case 'breakfast':
        return Coffee;
      case 'lunch':
        return Sun;
      case 'dinner':
        return Moon;
      case 'snack':
        return Cookie;
      default:
        return UtensilsCrossed;
    }
  };

  const getMealTypeColor = (mealType) => {
    switch (mealType) {
      case 'breakfast':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'lunch':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'dinner':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'snack':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTime = (mealType) => {
    switch (mealType) {
      case 'breakfast':
        return '7:00 AM - 9:00 AM';
      case 'lunch':
        return '12:00 PM - 2:00 PM';
      case 'dinner':
        return '6:00 PM - 8:00 PM';
      case 'snack':
        return 'Various Times';
      default:
        return 'TBD';
    }
  };

  const todaysMeals = meals.filter(meal => meal.date === new Date().toISOString().split('T')[0]);
  const totalServedToday = todaysMeals.reduce((sum, meal) => 
    sum + (meal.residents_adults_served || 0) + (meal.residents_children_served || 0) + 
    (meal.community_adults_served || 0) + (meal.community_children_served || 0), 0
  );
  const residentsServedToday = todaysMeals.reduce((sum, meal) => 
    sum + (meal.residents_adults_served || 0) + (meal.residents_children_served || 0), 0
  );
  const communityServedToday = todaysMeals.reduce((sum, meal) => 
    sum + (meal.community_adults_served || 0) + (meal.community_children_served || 0), 0
  );

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <UtensilsCrossed className="h-8 w-8 text-indigo-600" />
            <span>Meal Management</span>
          </h1>
          <p className="text-gray-600 mt-1">Schedule and track meals served at the shelter</p>
        </div>
        
        <div className="flex space-x-3">
          <Button
            onClick={() => setShowReportDialog(true)}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <BarChart3 className="h-4 w-4" />
            <span>Monthly Report</span>
          </Button>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Schedule Meal</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Schedule New Meal</DialogTitle>
              <DialogDescription>
                Plan a meal service for residents and staff
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {dietaryAlerts.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="meal-allergy-alert">
                  <div className="flex items-center gap-2 text-red-800 font-semibold text-sm">
                    <AlertCircle className="h-4 w-4" />
                    Allergy & Dietary Alert — {dietaryAlerts.length} current resident{dietaryAlerts.length > 1 ? 's' : ''}
                  </div>
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {dietaryAlerts.map((r) => (
                      <div key={r.id} className="text-xs text-gray-800" data-testid={`meal-allergy-${r.id}`}>
                        <span className="font-medium">{r.first_name} {r.last_name}:</span>
                        {r.dietary_restrictions && r.dietary_restrictions.trim() && (
                          <span className="ml-1"><span className="text-red-700 font-medium">Dietary/Food:</span> {r.dietary_restrictions}</span>
                        )}
                        {r.other_allergies && r.other_allergies.trim() && (
                          <span className="ml-1"><span className="text-amber-700 font-medium">Other:</span> {r.other_allergies}</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-red-600">Review these before finalizing today's menu. Synced live from resident intake.</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Meal Type *</label>
                  <Select
                    value={formData.meal_type}
                    onValueChange={(value) => setFormData({...formData, meal_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="breakfast">
                        <div className="flex items-center space-x-2">
                          <Coffee className="h-4 w-4" />
                          <span>Breakfast</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="lunch">
                        <div className="flex items-center space-x-2">
                          <Sun className="h-4 w-4" />
                          <span>Lunch</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="dinner">
                        <div className="flex items-center space-x-2">
                          <Moon className="h-4 w-4" />
                          <span>Dinner</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="snack">
                        <div className="flex items-center space-x-2">
                          <Cookie className="h-4 w-4" />
                          <span>Snack</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Date *</label>
                  <Input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Menu Items *</label>
                <div className="space-y-2">
                  {formData.menu_items.map((item, index) => (
                    <div key={index} className="flex space-x-2">
                      <Input
                        value={item}
                        onChange={(e) => updateMenuItem(index, e.target.value)}
                        placeholder="Enter menu item"
                        className="flex-1"
                      />
                      {formData.menu_items.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => removeMenuItem(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addMenuItem}
                    className="w-full"
                  >
                    + Add Menu Item
                  </Button>
                </div>
              </div>

              {/* Ingredients, Portion Planning & Cost */}
              <div className="border rounded-lg p-3 bg-gray-50 space-y-3" data-testid="meal-ingredients-section">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm font-medium text-gray-700">Ingredients & Cost</label>
                  <Input
                    type="number" min="0" className="w-44"
                    placeholder="Expected attendance"
                    value={formData.expected_attendance}
                    onChange={(e) => setFormData({ ...formData, expected_attendance: e.target.value })}
                    data-testid="meal-attendance"
                  />
                </div>
                {formData.ingredients.map((ing, i) => (
                  <div key={i} className="space-y-1">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-3">
                        <Select value={ing.inventory_item_id || 'custom'} onValueChange={(v) => updateIngredient(i, 'inventory_item_id', v === 'custom' ? '' : v)}>
                          <SelectTrigger data-testid={`ing-item-${i}`}><SelectValue placeholder="Source" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">Custom item</SelectItem>
                            {inventory.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input className="col-span-2" placeholder="Name" value={ing.name} onChange={(e) => updateIngredient(i, 'name', e.target.value)} data-testid={`ing-name-${i}`} />
                      <Input className="col-span-2" type="number" placeholder="Qty" value={ing.quantity} onChange={(e) => updateIngredient(i, 'quantity', e.target.value)} data-testid={`ing-qty-${i}`} />
                      <Input className="col-span-2" type="number" step="0.01" placeholder="$/unit" value={ing.unit_cost} onChange={(e) => updateIngredient(i, 'unit_cost', e.target.value)} data-testid={`ing-cost-${i}`} />
                      <Input className="col-span-2" type="number" step="0.01" placeholder="Portion/person" value={ing.portion_per_person} onChange={(e) => updateIngredient(i, 'portion_per_person', e.target.value)} title="Portion per person" />
                      <button type="button" className="col-span-1 text-red-500 font-bold" onClick={() => removeIngredient(i)} data-testid={`remove-ing-${i}`}>✕</button>
                    </div>
                    {ing.portion_per_person && parseInt(formData.expected_attendance) > 0 && (
                      <div className="text-xs text-indigo-600">Suggested: {(parseFloat(ing.portion_per_person) * parseInt(formData.expected_attendance)).toFixed(1)} {ing.unit} for {formData.expected_attendance} people</div>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addIngredient} data-testid="add-ingredient-btn">+ Add ingredient</Button>
                {formData.ingredients.length > 0 && (
                  <div className="flex justify-between text-sm font-medium pt-2 border-t">
                    <span>Total food cost: <span className="text-indigo-700" data-testid="live-food-cost">${liveMealCost.toFixed(2)}</span></span>
                    {parseInt(formData.expected_attendance) > 0 && <span>Cost/serving: <span className="text-indigo-700">${liveCostPerServing.toFixed(2)}</span></span>}
                  </div>
                )}
              </div>

              
              <div>
                <label className="text-sm font-medium text-gray-700">Assigned Residents (Kitchen Duty)</label>
                <div className="mt-2 max-h-32 overflow-y-auto border rounded-lg p-2">
                  {residents.map((resident) => (
                    <label key={resident.id} className="flex items-center space-x-2 p-1">
                      <input
                        type="checkbox"
                        checked={formData.assigned_residents.includes(resident.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              assigned_residents: [...formData.assigned_residents, resident.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              assigned_residents: formData.assigned_residents.filter(id => id !== resident.id)
                            });
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{resident.first_name} {resident.last_name}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Notes</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any special notes about this meal service..."
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                  Schedule Meal
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Date Selection and Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Date Selection</p>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="mt-2"
                />
              </div>
              <Calendar className="h-8 w-8 text-indigo-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Meals Today</p>
                <p className="text-2xl font-bold text-gray-900">{todaysMeals.length}</p>
              </div>
              <UtensilsCrossed className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Served Today</p>
                <p className="text-2xl font-bold text-gray-900">{totalServedToday}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Residents Today</p>
                <p className="text-2xl font-bold text-gray-900">{residentsServedToday}</p>
              </div>
              <Users className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Community Today</p>
                <p className="text-2xl font-bold text-gray-900">{communityServedToday}</p>
              </div>
              <Users className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Meals List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="card-shadow">
              <CardContent className="p-6">
                <div className="skeleton h-32 w-full rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : meals.length === 0 ? (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <UtensilsCrossed className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No meals scheduled</h3>
            <p className="text-gray-600 mb-4">No meals have been scheduled for {selectedDate}.</p>
            <Button onClick={() => setShowAddDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="h-4 w-4 mr-2" />
              Schedule First Meal
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {meals.map((meal) => {
            const MealIcon = getMealIcon(meal.meal_type);
            
            return (
              <Card key={meal.id} className="card-shadow hover:card-shadow-lg transition-shadow">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <MealIcon className="h-6 w-6 text-indigo-600" />
                      <CardTitle className="text-lg capitalize">
                        {meal.meal_type}
                      </CardTitle>
                    </div>
                    <Badge className={`px-3 py-1 ${getMealTypeColor(meal.meal_type)}`}>
                      {formatTime(meal.meal_type)}
                    </Badge>
                  </div>
                  <CardDescription className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(meal.date + 'T00:00:00').toLocaleDateString()}</span>
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Menu Items */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center space-x-2">
                      <ChefHat className="h-4 w-4" />
                      <span>Menu</span>
                    </h4>
                    <div className="space-y-1">
                      {meal.menu_items.map((item, index) => (
                        <div key={index} className="text-sm text-gray-600 pl-2 border-l-2 border-gray-200">
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Served Count with Breakdown */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center space-x-2">
                      <Users className="h-4 w-4" />
                      <span>Meals Served</span>
                    </h4>
                    
                    {/* Total Count */}
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <span className="font-medium text-blue-900">Total Served</span>
                      <span className="text-2xl font-bold text-blue-800">
                        {(meal.residents_adults_served || 0) + (meal.residents_children_served || 0) + 
                         (meal.community_adults_served || 0) + (meal.community_children_served || 0)}
                      </span>
                    </div>

                    {/* Breakdown */}
                    {((meal.residents_adults_served || 0) + (meal.residents_children_served || 0) + 
                      (meal.community_adults_served || 0) + (meal.community_children_served || 0)) > 0 && (
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="p-2 bg-green-50 rounded border">
                          <div className="font-medium text-green-900">Residents</div>
                          <div className="text-green-700">
                            Adults: {meal.residents_adults_served || 0} | Children: {meal.residents_children_served || 0}
                          </div>
                        </div>
                        <div className="p-2 bg-purple-50 rounded border">
                          <div className="font-medium text-purple-900">Community</div>
                          <div className="text-purple-700">
                            Adults: {meal.community_adults_served || 0} | Children: {meal.community_children_served || 0}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Assigned Residents */}
                  {meal.assigned_residents.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2 text-sm">Kitchen Duty:</h4>
                      <div className="flex flex-wrap gap-1">
                        {meal.assigned_residents.slice(0, 3).map((residentId) => {
                          const resident = residents.find(r => r.id === residentId);
                          return resident ? (
                            <Badge key={residentId} variant="outline" className="text-xs">
                              {resident.first_name}
                            </Badge>
                          ) : null;
                        })}
                        {meal.assigned_residents.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{meal.assigned_residents.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Food cost & ingredients */}
                  {(meal.total_food_cost > 0 || (meal.ingredients && meal.ingredients.length > 0)) && (
                    <div className="p-3 bg-indigo-50 rounded-lg space-y-2" data-testid={`meal-cost-${meal.id}`}>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Food cost</span>
                        <span className="font-semibold text-indigo-700">${(meal.total_food_cost || 0).toFixed(2)}</span>
                      </div>
                      {meal.cost_per_serving > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Cost / serving</span>
                          <span className="font-semibold text-indigo-700">${meal.cost_per_serving.toFixed(2)}</span>
                        </div>
                      )}
                      {meal.ingredients && meal.ingredients.length > 0 && (
                        <div className="text-xs text-gray-600">{meal.ingredients.map((g) => g.name).join(', ')}</div>
                      )}
                      {meal.ingredients && meal.ingredients.some((g) => g.inventory_item_id) && (
                        meal.inventory_deducted ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">Inventory deducted</Badge>
                        ) : (
                          <Button size="sm" variant="outline" className="w-full mt-1" onClick={() => deductInventory(meal)} data-testid={`deduct-inventory-${meal.id}`}>
                            Deduct ingredients from inventory
                          </Button>
                        )
                      )}
                    </div>
                  )}


                  {/* Notes */}
                  {meal.notes && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">{meal.notes}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="pt-4 border-t">
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => {
                          setSelectedMeal(meal);
                          setShowServeDialog(true);
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Users className="h-4 w-4 mr-2" />
                        Record Served
                      </Button>
                      <Button
                        onClick={() => {
                          setMealToDelete(meal);
                          setShowDeleteDialog(true);
                        }}
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Serve Meals Dialog */}
      <Dialog open={showServeDialog} onOpenChange={setShowServeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Meals Served</DialogTitle>
            <DialogDescription>
              {selectedMeal ? `${selectedMeal.meal_type.charAt(0).toUpperCase() + selectedMeal.meal_type.slice(1)} - ${new Date(selectedMeal.date + 'T00:00:00').toLocaleDateString()}` : ''}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Residents Section */}
            <div className="space-y-3">
              <h4 className="font-semibold text-green-900 flex items-center space-x-2">
                <Users className="h-4 w-4" />
                <span>Shelter Residents</span>
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Adults</label>
                  <Input
                    type="number"
                    min="0"
                    max="999"
                    value={serveData.residents_adults}
                    onChange={(e) => setServeData({
                      ...serveData,
                      residents_adults: parseInt(e.target.value) || 0
                    })}
                    className="text-lg text-center"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Children</label>
                  <Input
                    type="number"
                    min="0"
                    max="999"
                    value={serveData.residents_children}
                    onChange={(e) => setServeData({
                      ...serveData,
                      residents_children: parseInt(e.target.value) || 0
                    })}
                    className="text-lg text-center"
                  />
                </div>
              </div>
              <div className="text-right text-sm text-gray-600">
                Subtotal: {serveData.residents_adults + serveData.residents_children} residents
              </div>
            </div>

            {/* Community Section */}
            <div className="space-y-3">
              <h4 className="font-semibold text-purple-900 flex items-center space-x-2">
                <Users className="h-4 w-4" />
                <span>Community Members</span>
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Adults</label>
                  <Input
                    type="number"
                    min="0"
                    max="999"
                    value={serveData.community_adults}
                    onChange={(e) => setServeData({
                      ...serveData,
                      community_adults: parseInt(e.target.value) || 0
                    })}
                    className="text-lg text-center"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Children</label>
                  <Input
                    type="number"
                    min="0"
                    max="999"
                    value={serveData.community_children}
                    onChange={(e) => setServeData({
                      ...serveData,
                      community_children: parseInt(e.target.value) || 0
                    })}
                    className="text-lg text-center"
                  />
                </div>
              </div>
              <div className="text-right text-sm text-gray-600">
                Subtotal: {serveData.community_adults + serveData.community_children} community
              </div>
            </div>

            {/* Total */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-blue-900">Total Meals to Record:</span>
                <span className="text-2xl font-bold text-blue-800">
                  {serveData.residents_adults + serveData.residents_children + 
                   serveData.community_adults + serveData.community_children}
                </span>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => {
                setShowServeDialog(false);
                setSelectedMeal(null);
                setServeData({
                  residents_adults: 0,
                  residents_children: 0,
                  community_adults: 0,
                  community_children: 0
                });
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleServeMeal}
                className="bg-green-600 hover:bg-green-700"
                disabled={serveData.residents_adults + serveData.residents_children + 
                         serveData.community_adults + serveData.community_children === 0}
              >
                Record Meals
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Meal</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this meal? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {mealToDelete && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 capitalize">
                  {mealToDelete.meal_type} - {new Date(mealToDelete.date + 'T00:00:00').toLocaleDateString()}
                </h4>
                <div className="mt-2 text-sm text-gray-600">
                  <strong>Menu:</strong> {mealToDelete.menu_items.join(', ')}
                </div>
                {mealToDelete.notes && (
                  <div className="mt-1 text-sm text-gray-600">
                    <strong>Notes:</strong> {mealToDelete.notes}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={() => {
                  setShowDeleteDialog(false);
                  setMealToDelete(null);
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleDeleteMeal}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Meal
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Monthly Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Monthly Meal Service Report</DialogTitle>
            <DialogDescription>
              Comprehensive report for {new Date(reportPeriod.year, reportPeriod.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Report Period Selection */}
            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-700">Month</label>
                <Select
                  value={reportPeriod.month.toString()}
                  onValueChange={(value) => setReportPeriod({...reportPeriod, month: parseInt(value)})}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({length: 12}, (_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Year</label>
                <Select
                  value={reportPeriod.year.toString()}
                  onValueChange={(value) => setReportPeriod({...reportPeriod, year: parseInt(value)})}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({length: 5}, (_, i) => {
                      const year = new Date().getFullYear() - 2 + i;
                      return (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={generateMonthlyReport} className="bg-indigo-600 hover:bg-indigo-700">
                Generate Report
              </Button>
            </div>

            {/* Report Content */}
            {reportData && (
              <div className="space-y-6">
                {/* Summary Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">{reportData.totalMeals}</div>
                      <div className="text-sm text-gray-600">Total Meals</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">{reportData.totalServed}</div>
                      <div className="text-sm text-gray-600">People Served</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">{reportData.totalResidents}</div>
                      <div className="text-sm text-gray-600">Residents</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold text-orange-600">{reportData.totalCommunity}</div>
                      <div className="text-sm text-gray-600">Community</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Meal Type Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>Meal Type Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-orange-50 rounded-lg">
                        <div className="text-xl font-bold text-orange-600">{reportData.breakfastCount}</div>
                        <div className="text-sm text-gray-600">Breakfast</div>
                      </div>
                      <div className="text-center p-3 bg-yellow-50 rounded-lg">
                        <div className="text-xl font-bold text-yellow-600">{reportData.lunchCount}</div>
                        <div className="text-sm text-gray-600">Lunch</div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <div className="text-xl font-bold text-purple-600">{reportData.dinnerCount}</div>
                        <div className="text-sm text-gray-600">Dinner</div>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-xl font-bold text-green-600">{reportData.snackCount}</div>
                        <div className="text-sm text-gray-600">Snacks</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Daily Breakdown Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Daily Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Date</th>
                            <th className="text-center p-2">Breakfast</th>
                            <th className="text-center p-2">Lunch</th>
                            <th className="text-center p-2">Dinner</th>
                            <th className="text-center p-2">Snacks</th>
                            <th className="text-center p-2">Total Served</th>
                            <th className="text-center p-2">Residents</th>
                            <th className="text-center p-2">Community</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(reportData.dailyBreakdown).sort().map(([date, data]) => (
                            <tr key={date} className="border-b hover:bg-gray-50">
                              <td className="p-2">{new Date(date + 'T00:00:00').toLocaleDateString()}</td>
                              <td className="text-center p-2">{data.breakfast}</td>
                              <td className="text-center p-2">{data.lunch}</td>
                              <td className="text-center p-2">{data.dinner}</td>
                              <td className="text-center p-2">{data.snack}</td>
                              <td className="text-center p-2 font-semibold">{data.totalServed}</td>
                              <td className="text-center p-2 text-green-600">{data.residents}</td>
                              <td className="text-center p-2 text-purple-600">{data.community}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Export Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button variant="outline" onClick={printReport}>
                    Print Report
                  </Button>
                  <Button onClick={exportReportToCSV} className="bg-green-600 hover:bg-green-700">
                    Export to CSV
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MealManagement;